import React, { useMemo } from 'react'
import { Tabs, Tab } from '@fs/zion-ui'
import { useLocation } from '@fs/zion-router'
import { useTranslation } from 'react-i18next'
import useAppTabs from '../hooks/useAppTabs'

const AppTabs = ({ showAdmin, showReports, showLabs }) => {
  // bindI18n ensures component re-renders when language changes or translations load
  const { i18n } = useTranslation(undefined, { bindI18n: 'languageChanged loaded' })

  // Use getFixedT to get a fresh translation function for the current language
  // This works around an issue where the t() from useTranslation may be stale
  // Memoize to avoid creating a new function on every render
  const t = useMemo(() => i18n.getFixedT(i18n.language), [i18n])

  const { currentTabIndex, showTabs, visibleTabs } = useAppTabs({
    showAdmin,
    showReports,
    showLabs,
  })
  const { pathname } = useLocation()
  const isVerifyPlaces = pathname.includes('verify-places')
  const isRecordHints = pathname.includes('record-hints')
  const isEventPage =
    /^\/events(\/|$)/.test(pathname) &&
    !/^\/events\/manager(\/|$)/.test(pathname) &&
    !/^\/events\/[^/]+\/user(\/|$)/.test(pathname)
  const isQuickNameReview = pathname.includes('quick-name-review')
  const isFullNameReview = pathname.includes('full-name-review')

  const tabs = useMemo(() => {
    const visibleTabsSet = new Set(visibleTabs.map((tab) => tab.path))
    return [
      {
        to: '/',
        id: 'overview',
        linkName: 'overview',
        title: t('v2_tabs_overview'),
        dataTestId: 'tablist-tab-overview',
        show: visibleTabsSet.has('/'),
      },
      {
        to: '/opportunities',
        id: 'opportunities',
        linkName: 'opportunities',
        title: t('v2_tabs_opportunities', 'Opportunities'),
        dataTestId: 'tablist-tab-opportunities',
        show: visibleTabsSet.has('/opportunities'),
      },
      {
        to: '/your-impact',
        id: 'impact',
        linkName: 'impact',
        title: t('v2-tabs-your-impact', 'Your Impact'),
        dataTestId: 'tablist-tab-impact',
        show: visibleTabsSet.has('/your-impact'),
      },
      {
        to: '/indexing',
        id: 'indexing',
        linkName: 'indexing',
        title: t('v2_tabs_indexing', 'What Is Indexing?'),
        dataTestId: 'tablist-tab-indexing',
        show: visibleTabsSet.has('/indexing'),
      },
      {
        to: '/reports',
        id: 'reports',
        linkName: 'reports',
        title: t('reports.tab-heading', 'Reports'),
        dataTestId: 'tablist-tab-reports',
        show: visibleTabsSet.has('/reports'),
      },
      {
        to: '/prison-reports',
        id: 'prison-reports',
        linkName: 'prison-reports',
        title: t('prison-reports.tab-heading', 'Prison Reports'),
        dataTestId: 'tablist-tab-prison-reports',
        show: visibleTabsSet.has('/prison-reports'),
      },
      {
        to: '/admin',
        id: 'admin',
        linkName: 'admin-console',
        title: t('admin.tab-heading', 'Admin'),
        dataTestId: 'tablist-tab-admin',
        show: visibleTabsSet.has('/admin'),
      },
      {
        to: '/labs',
        id: 'labs',
        linkName: 'give-labs',
        title: t('labs.tab-heading', 'GIVE Labs'),
        dataTestId: 'tablist-tab-labs',
        show: visibleTabsSet.has('/labs'),
      },
    ].filter((tab) => tab.show)
  }, [t, visibleTabs])

  if (!showTabs || isEventPage || isVerifyPlaces || isRecordHints || isQuickNameReview || isFullNameReview) {
    return null
  }

  return (
    // biome-ignore lint/correctness/useUniqueElementIds: the id is unique within the scope of the page
    <Tabs index={currentTabIndex} data-testid="tablist" id="app-tabs">
      {tabs.map((tab) => (
        <Tab key={tab.id} to={tab.to} linkName={tab.linkName} title={tab.title} data-testid={tab.dataTestId} />
      ))}
    </Tabs>
  )
}

export default AppTabs
